import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  Segment,
  Button,
  Menu,
  Table,
  Dimmer,
  Loader,
  Dropdown,
  Input,
  Icon,
  Pagination,
} from 'semantic-ui-react';
import { getMemberList } from '../api/MemberManagementApi';
import { MemberListDTO, MemberDTO } from '../dto/MemberManagementDTOs';

const MemberManagementPage: React.FC = () => {
  // TODO: 실제 인증 토큰을 localStorage나 context에서 가져오도록 수정
  const authToken = localStorage.getItem('accessToken') || '';

  // 날짜 포맷팅 함수 (YYYY-MM-DD)
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 내일 날짜 계산
  const getTomorrow = (): string => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDate(tomorrow);
  };

  // N일 전 날짜 계산
  const getDaysAgo = (days: number): string => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return formatDate(date);
  };

  // 오늘 날짜 계산
  const getToday = (): string => {
    return formatDate(new Date());
  };

  // 초기 날짜 계산
  const getInitialStartDate = (): string => {
    return getToday();
  };

  const getInitialEndDate = (): string => {
    return getTomorrow();
  };

  const [loading, setLoading] = useState(true);
  const [memberListData, setMemberListData] = useState<MemberListDTO | null>(null);
  const [startDate, setStartDate] = useState(getInitialStartDate());
  const [endDate, setEndDate] = useState(getInitialEndDate());
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [dateInputEnabled, setDateInputEnabled] = useState(false);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string | undefined>('all');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    
    if (period === 'today') {
      setDateInputEnabled(false);
      setStartDate(getToday());
      setEndDate(getTomorrow());
    } else if (period === '7days') {
      setDateInputEnabled(false);
      setStartDate(getDaysAgo(7));
      setEndDate(getTomorrow());
    } else if (period === '30days') {
      setDateInputEnabled(false);
      setStartDate(getDaysAgo(30));
      setEndDate(getTomorrow());
    } else if (period === 'all') {
      setDateInputEnabled(true);
    }
  };

  const ageGroupOptions = [
    { key: 'all', text: '전체', value: 'all' },
    { key: '10s', text: '10대', value: '10s' },
    { key: '20s30s', text: '20,30대', value: '20s30s' },
    { key: '40s50s', text: '40,50대', value: '40s50s' },
    { key: '60s', text: '60대이상', value: '60s' },
  ];

  const statusOptions = [
    { key: 'all', text: '전체', value: 'all' },
    { key: 'normal', text: '정상', value: 'NORMAL' },
    { key: 'suspended', text: '정지', value: 'SUSPENDED' },
  ];

  const loadMemberList = async () => {
    setLoading(true);
    try {
      const ageGroup = selectedAgeGroup === 'all' ? undefined : selectedAgeGroup;
      const status = selectedStatus === 'all' ? undefined : selectedStatus;
      const keyword = searchKeyword.trim() || undefined;
      
      const result = await getMemberList(
        authToken,
        startDate,
        endDate,
        ageGroup,
        keyword,
        status,
        currentPage,
        pageSize
      );
      if (result && result.errorCode === 0) {
        setMemberListData(result.response);
      }
    } catch (error) {
      console.error('회원 목록 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMemberList();
  }, [currentPage]);

  useEffect(() => {
    // 필터 변경 시 첫 페이지로 리셋
    setCurrentPage(1);
    loadMemberList();
  }, [startDate, endDate, selectedAgeGroup, selectedStatus]);

  const handleSearch = () => {
    setCurrentPage(1);
    loadMemberList();
  };

  const handlePageChange = (e: React.MouseEvent, { activePage }: any) => {
    setCurrentPage(activePage);
  };

  const handleMemberDetail = (memberNo: number) => {
    // TODO: 회원 상세 페이지로 이동
    console.log('회원 상세:', memberNo);
  };

  const handleMemberSuspend = (memberNo: number) => {
    // TODO: 회원 정지 기능 구현
    console.log('회원 정지:', memberNo);
  };

  if (loading && !memberListData) {
    return (
      <Container fluid>
        <Dimmer active inverted>
          <Loader size="large">데이터를 불러오는 중...</Loader>
        </Dimmer>
      </Container>
    );
  }

  if (!memberListData) {
    return (
      <Container fluid>
        <Header as="h2">데이터를 불러올 수 없습니다.</Header>
      </Container>
    );
  }

  return (
    <Container fluid>
      {/* 상단 제목 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Header as="h2" style={{ margin: 0 }}>
          5. 회원 관리
        </Header>
        <Icon name="bell" size="large" style={{ cursor: 'pointer' }} />
      </div>

      {/* 필터 영역 */}
      <Segment
        raised
        style={{
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f8f9ff',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        {/* 기간 선택 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ marginRight: 8, fontWeight: 500 }}>기간</span>
          <Menu compact>
            <Menu.Item
              active={selectedPeriod === 'today'}
              onClick={() => handlePeriodChange('today')}
            >
              오늘
            </Menu.Item>
            <Menu.Item
              active={selectedPeriod === '7days'}
              onClick={() => handlePeriodChange('7days')}
            >
              7일
            </Menu.Item>
            <Menu.Item
              active={selectedPeriod === '30days'}
              onClick={() => handlePeriodChange('30days')}
            >
              30일
            </Menu.Item>
            <Menu.Item
              active={selectedPeriod === 'all'}
              onClick={() => handlePeriodChange('all')}
            >
              전체
            </Menu.Item>
          </Menu>
        </div>

        {/* 연령대별 드롭다운 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 500 }}>연령대별:</span>
          <Dropdown
            selection
            options={ageGroupOptions}
            value={selectedAgeGroup}
            onChange={(e, { value }) => setSelectedAgeGroup(value as string | undefined)}
            style={{ minWidth: 120 }}
          />
        </div>

        {/* 검색 입력 */}
        <Input
          icon="search"
          iconPosition="left"
          placeholder="Q 아이디/닉네임 검색"
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              handleSearch();
            }
          }}
          style={{ minWidth: 200 }}
        />

        {/* 상태 드롭다운 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 500 }}>상태:</span>
          <Dropdown
            selection
            options={statusOptions}
            value={selectedStatus}
            onChange={(e, { value }) => setSelectedStatus(value as string | undefined)}
            style={{ minWidth: 120 }}
          />
        </div>
      </Segment>

      {/* 회원 목록 테이블 */}
      <Segment raised style={{ padding: '20px', marginBottom: 24 }}>
        <Header as="h4" style={{ marginBottom: 16, marginTop: 0 }}>
          전체 회원 목록
        </Header>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>No.</Table.HeaderCell>
              <Table.HeaderCell>닉네임</Table.HeaderCell>
              <Table.HeaderCell>아이디</Table.HeaderCell>
              <Table.HeaderCell>가입일</Table.HeaderCell>
              <Table.HeaderCell>최근 접속일</Table.HeaderCell>
              <Table.HeaderCell>독서/필사</Table.HeaderCell>
              <Table.HeaderCell>상태</Table.HeaderCell>
              <Table.HeaderCell>관리</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {memberListData.members.map((member, index) => (
              <Table.Row key={member.memberNo}>
                <Table.Cell>{(currentPage - 1) * pageSize + index + 1}</Table.Cell>
                <Table.Cell>{member.nickname}</Table.Cell>
                <Table.Cell>{member.userId}</Table.Cell>
                <Table.Cell>{member.joinDate}</Table.Cell>
                <Table.Cell>{member.lastAccessDate}</Table.Cell>
                <Table.Cell>{member.readingCount}/{member.scriptingCount}</Table.Cell>
                <Table.Cell>
                  <Button
                    size="small"
                    color={member.status === 'NORMAL' ? 'green' : 'red'}
                    style={{ minWidth: 60 }}
                  >
                    {member.status === 'NORMAL' ? '정상' : '정지'}
                  </Button>
                </Table.Cell>
                <Table.Cell>
                  <span
                    style={{ color: '#0066cc', cursor: 'pointer', marginRight: 12 }}
                    onClick={() => handleMemberDetail(member.memberNo)}
                  >
                    상세
                  </span>
                  <span
                    style={{ color: '#0066cc', cursor: 'pointer' }}
                    onClick={() => handleMemberSuspend(member.memberNo)}
                  >
                    정지
                  </span>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>

        {/* 페이지네이션 */}
        {memberListData.page && memberListData.page.last > 1 && (
          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              activePage={currentPage}
              totalPages={memberListData.page.last}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Segment>
    </Container>
  );
};

export default MemberManagementPage;
